import {
  BulkActionCount,
  DisplayFieldErrors,
  FormattedDate,
  ViewContext,
  useTableSelection,
} from "melco-shared-logic";
import isEmpty from "lodash-es/isEmpty";
import {
  Button,
  DesignTokens,
  FullWidthSpace,
  PlusIcon,
  Space,
  Table,
  Title,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TFunction } from "i18next";
import dayjs from "dayjs";
import { useField } from "formik";
import { BulkActionRemoveGroups } from "./BulkActionRemoveGroups";

const { colors, spacings } = DesignTokens;

const EmptyMachineWrapper = styled.div`
  border: 1px dashed ${colors.gray.middle};
  padding: ${spacings.middle}px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TableWrapper = styled.div`
  margin-left: -${spacings.xlarge}px;
  margin-right: -${spacings.xlarge}px;
`;

const AddButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

type MachineListProps = {
  setDrawerOpen: (open: boolean) => void;
};

export const machineListColumns = (
  t: TFunction,
  context: ViewContext = ViewContext.List
) => {
  // use local sorting for ViewContext.List
  const sorter = (dataIndex: string) => {
    if (context === ViewContext.List) {
      return (a: any, b: any) =>
        String(a[dataIndex]).localeCompare(String(b[dataIndex]));
    }

    return true;
  };

  const dateSorter = (dataIndex: string) => {
    if (context === ViewContext.List) {
      return (a: any, b: any) =>
        dayjs(a[dataIndex]).unix() - dayjs(b[dataIndex]).unix();
    }
    return true;
  };

  return [
    {
      title: t("reporting.machinestatus.view.machinegroups.list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: sorter("name"),
    },
    {
      title: t(
        "reporting.machinestatus.view.machinegroups.list.columns.machines"
      ),
      key: "machine_count",
      dataIndex: "machine_count",
      sorter: sorter("machine_count"),
    },
    {
      title: t(
        "reporting.machinestatus.view.machinegroups.list.columns.created"
      ),
      key: "created_date",
      sorter: dateSorter("created_date"),
      render: (item: any) => <FormattedDate date={item.created_date} />,
    },
    {
      title: t(
        "reporting.machinestatus.view.machinegroups.list.columns.last_modified"
      ),
      key: "updated_date",
      //dataIndex: "last_sync_date",
      sorter: dateSorter("updated_date"),
      render: (item: any) => <FormattedDate date={item.updated_date} />,
    },
  ];
};

export const MachineStatusMachineGroupsList: React.FC<MachineListProps> = ({
  setDrawerOpen,
}) => {
  const { t } = useTranslation();
  const { selectedRowKeys, setSelectedRowKeys, rowSelection } =
    useTableSelection<any>(() => {});
  const [{ value: groups }] = useField<any[]>("group_list");

  const title = (
    <Title level={5}>
      {t("reporting.machinestatus.assigned_machine_groups")}
    </Title>
  );

  const addButton = (
    <Button
      type="primary"
      icon={<PlusIcon />}
      onClick={() => setDrawerOpen(true)}
      data-cy-id="machinestatus-view-add-machine-group-button"
    >
      {t("reporting.machinestatus.add_machine_group_button")}
    </Button>
  );

  if (isEmpty(groups)) {
    return (
      <>
        {title}
        <DisplayFieldErrors name="group_list" />

        <EmptyMachineWrapper>{addButton}</EmptyMachineWrapper>
      </>
    );
  }

  const columns = [...machineListColumns(t)];

  return (
    <>
      <TableHeader>
        {title}

        <Space size="middle">
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionRemoveGroups
            //  allMachine={group_list}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </Space>
      </TableHeader>

      <DisplayFieldErrors name="group_list" />
      {/* <DisplayFieldErrors name="name" /> */}
      {/* {JSON.stringify(group_list)} */}
      <FullWidthSpace direction="vertical" size="large">
        <TableWrapper>
          <Table
            showSorterTooltip={false}
            dataSource={groups}
            columns={columns}
            rowKey="id"
            pagination={false}
            rowSelection={rowSelection}
            data-cy="group-table"
          />
        </TableWrapper>
        <AddButtonWrapper>{addButton}</AddButtonWrapper>
      </FullWidthSpace>
    </>
  );
};
